// ═══════════════════════════════════════════════════════════════════════════
// PAGE: VOCABULAIRE MÉTIER (admin) — option C, 2026-05-08
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// 2 sections :
//   1. **Vocabulaire général** (category=vocabulary) — noms d'objets isolés
//      (Risque, Action, RACI, Sévérité). Input court. Renommer un mot ici
//      le propage partout où il apparaît seul dans l'app via t().
//   2. **Phrases personnalisables** (category=phrases) — phrases de mode
//      opératoire (intros, helpers, sous-titres de wizards). Textarea
//      (multi-lignes). **Vouvoiement obligatoire** (cf. memory).
//
// Architecture rappel :
// - Catalog = fichier statique versionné Git (source unique des défauts).
// - Overrides = table tenant `i18n_overrides`, éditée par cette page.
// - Whitelist côté backend (EDITABLE_VOCAB ∪ EDITABLE_PHRASES). Toute
//   tentative d'écriture sur une clé hors whitelist → 400.
// - Le backend retourne `category` par item — le front partitionne en
//   2 sections distinctes pour la lisibilité.
//
// UX : on charge la liste avec défauts + overrides courants, diff calculé
// côté front pour ne POST que les valeurs effectivement modifiées.
// Bouton "↺" par ligne pour revenir au défaut (DELETE de l'override).
// La section "Phrases personnalisables" est repliée par défaut pour ne
// pas surcharger l'UI à l'arrivée sur la page.

import { buildVocabSectionHtml, buildPhrasesSectionHtml } from './page-vocabulary-fragments.js';

export interface VocabItem {
  key: string;
  default: string;
  current: string | null;
  category: 'vocabulary' | 'phrases' | string;
}

interface VocabOverridesData {
  locale: string;
  items: VocabItem[];
}

interface OverrideToSave {
  key: string;
  value: string;
}

declare global {
  interface Window {
    renderVocabularyPage: typeof renderVocabularyPage;
  }
}

pages.adminVocabulary = async function () {
  setHeader(t('vocabulary.label') || 'Vocabulaire métier', '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const data = (await api.getI18nOverrides('fr')) as VocabOverridesData;
    renderVocabularyPage(data);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderVocabularyPage(initialData: VocabOverridesData): void {
  const canWrite = !!(window.hasPermission && window.hasPermission('vocabulary.write'));
  const locale = initialData.locale;

  const initial = new Map<string, string>();
  for (const it of initialData.items) {
    initial.set(it.key, it.current != null ? it.current : '');
  }

  const vocabItems = initialData.items.filter((it) => it.category === 'vocabulary');
  const phraseItems = initialData.items.filter((it) => it.category === 'phrases');

  const VOCAB_GROUPS: ReadonlyArray<{ prefix: string; label: string }> = [
    { prefix: 'vocabulary.', label: 'Vocabulaire général' },
    { prefix: 'raci.', label: 'RACI' },
    { prefix: 'severity.', label: 'Sévérités' },
  ];

  const vocabGroups = VOCAB_GROUPS.map((g) => ({
    ...g,
    items: vocabItems.filter((it) => it.key.startsWith(g.prefix)),
  })).filter((g) => g.items.length > 0);

  // Sections vocabulaire + phrases : rendu pur délégué à page-vocabulary-fragments
  // (`canWrite` immuable threadé).
  const vocabSectionHtml = buildVocabSectionHtml(vocabGroups, canWrite);
  const phrasesSectionHtml = buildPhrasesSectionHtml(phraseItems, canWrite);

  const saveBtn = canWrite
    ? `<button class="btn btn-primary" id="btn-vocab-save" disabled>${esc(t('common.save'))}</button>`
    : '';
  const lockNotice = canWrite
    ? ''
    : `<p style="color:var(--text3);font-size:12px;margin-top:12px">
         Lecture seule — vous n'avez pas la permission <code>vocabulary.write</code>.
       </p>`;

  const totalItems = initialData.items.length;

  setContent(`
    <p style="font-size:13px;color:var(--text3);margin:8px 0 16px 0">
      Adaptez le vocabulaire UI au lexique de votre organisation. Les valeurs
      modifiées remplacent les défauts pour tous les utilisateurs du tenant.
      Le bouton <strong>↺</strong> rétablit le défaut sur une clé.
      Les modifications prennent effet au prochain rechargement complet
      d'une page (Cmd+R).
    </p>
    <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">
      <input type="search" id="vocab-search"
             placeholder="${esc(t('vocabulary.search.placeholder'))}"
             style="flex:1;max-width:480px;padding:7px 10px;font-size:13px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg2)">
      <span id="vocab-search-count" style="color:var(--text3);font-size:12px"></span>
    </div>
    ${vocabSectionHtml}
    ${phrasesSectionHtml}
    <div style="display:flex;gap:8px;align-items:center;margin-top:8px">
      ${saveBtn}
      <span id="vocab-status" style="color:var(--text3);font-size:12px"></span>
    </div>
    ${lockNotice}
  `);

  const root = document.getElementById('content');
  if (!root) return;

  const itemsByKey = new Map<string, VocabItem>();
  for (const it of initialData.items) itemsByKey.set(it.key, it);

  const searchEl = document.getElementById('vocab-search') as HTMLInputElement | null;
  const searchCountEl = document.getElementById('vocab-search-count');
  const phrasesDetails = root.querySelector<HTMLDetailsElement>('details.card');

  function applySearchFilter(query: string): void {
    if (!root) return;
    const q = (query || '').toLowerCase().trim();
    let matched = 0;
    const containers = root.querySelectorAll<HTMLElement>('tr[data-key], .vocab-phrase[data-key]');
    containers.forEach((c) => {
      const key = c.dataset['key'] || '';
      const it = itemsByKey.get(key);
      if (!q) {
        c.style.display = '';
        matched += 1;
        return;
      }
      const hay = (key + ' ' + (it?.default || '') + ' ' + (it?.current || '')).toLowerCase();
      const hit = hay.includes(q);
      c.style.display = hit ? '' : 'none';
      if (hit) matched += 1;
    });
    root.querySelectorAll<HTMLElement>('.card').forEach((card) => {
      const visibleRows = card.querySelectorAll(
        'tr[data-key]:not([style*="display: none"]), .vocab-phrase[data-key]:not([style*="display: none"])',
      );
      const hasRows = card.querySelectorAll('tr[data-key], .vocab-phrase[data-key]').length > 0;
      if (!hasRows) return;
      card.style.display = visibleRows.length > 0 ? '' : 'none';
    });
    if (q && phrasesDetails) {
      const phraseHits = phrasesDetails.querySelectorAll(
        '.vocab-phrase[data-key]:not([style*="display: none"])',
      ).length;
      if (phraseHits > 0) phrasesDetails.open = true;
    }
    if (searchCountEl) {
      if (!q) {
        searchCountEl.textContent = '';
      } else if (matched === 0) {
        searchCountEl.textContent = t('vocabulary.search.no_results', { query });
      } else {
        searchCountEl.textContent = t('vocabulary.search.match_count', {
          n: matched,
          total: totalItems,
        });
      }
    }
  }

  searchEl?.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    applySearchFilter(target?.value || '');
  });

  const onCmdK = (e: KeyboardEvent): void => {
    if (
      (e.metaKey || e.ctrlKey) &&
      e.key.toLowerCase() === 'k' &&
      document.getElementById('vocab-search')
    ) {
      e.preventDefault();
      const inp = document.getElementById('vocab-search') as HTMLInputElement | null;
      inp?.focus();
    }
  };
  document.addEventListener('keydown', onCmdK);
  registerPageDisposer(() => document.removeEventListener('keydown', onCmdK));

  if (!canWrite) return;

  const saveButton = document.getElementById('btn-vocab-save') as HTMLButtonElement | null;
  const statusEl = document.getElementById('vocab-status');
  if (!saveButton || !statusEl) return;

  function updateDiffState(): void {
    if (!root || !saveButton || !statusEl) return;
    const inputs = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('.vocab-input');
    let modified = 0;
    inputs.forEach((inp) => {
      const k = inp.dataset['key'] || '';
      const cur = inp.value;
      const init = initial.get(k) || '';
      if (cur !== init) modified += 1;
    });
    saveButton.disabled = modified === 0;
    statusEl.textContent = modified > 0 ? `${modified} modification(s) non enregistrée(s)` : '';
  }

  root.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (target?.classList.contains('vocab-input')) updateDiffState();
  });

  root.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const btn = target.closest<HTMLButtonElement>('.vocab-restore');
    if (!btn) return;
    const key = btn.dataset['key'] || '';
    btn.disabled = true;
    void (async () => {
      try {
        await api.deleteI18nOverride(locale, key);
        initial.set(key, '');
        const container = root.querySelector<HTMLElement>(
          `tr[data-key="${CSS.escape(key)}"], .vocab-phrase[data-key="${CSS.escape(key)}"]`,
        );
        const inp = container?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          '.vocab-input',
        );
        if (inp) inp.value = '';
        container?.classList.remove('vocab-row-overridden');
        btn.replaceWith(
          Object.assign(document.createElement('span'), {
            style: 'display:inline-block;width:32px',
          }),
        );
        toast(t('common.delete') + ' ✓');
        updateDiffState();
      } catch (err: unknown) {
        btn.disabled = false;
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    })();
  });

  saveButton.addEventListener('click', () => {
    void api.withButton(saveButton, async () => {
      const inputs = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('.vocab-input');
      const overrides: OverrideToSave[] = [];
      inputs.forEach((inp) => {
        const k = inp.dataset['key'] || '';
        const cur = inp.value;
        const init = initial.get(k) || '';
        if (cur !== init) overrides.push({ key: k, value: cur });
      });
      if (overrides.length === 0) return;
      try {
        await api.putI18nOverrides(locale, overrides);
        for (const o of overrides) {
          initial.set(o.key, o.value);
          const container = root.querySelector<HTMLElement>(
            `tr[data-key="${CSS.escape(o.key)}"], .vocab-phrase[data-key="${CSS.escape(o.key)}"]`,
          );
          if (container) {
            if (o.value === '') {
              container.classList.remove('vocab-row-overridden');
            } else {
              container.classList.add('vocab-row-overridden');
            }
          }
        }
        toast(t('vocabulary.toast.savedCount', { n: overrides.length }));
        updateDiffState();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    });
  });
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderVocabularyPage = renderVocabularyPage;

export {};
